import axios from "axios";

// Get backend URL from environment variable
// For Railway deployment, this must be set at build time
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  console.error("REACT_APP_BACKEND_URL is not set! API calls will fail.");
}

const API_URL = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// API functions
export const apiService = {
  // Auth
  login: (email, password) => api.post("/auth/login", { email, password }),
  // Note: register is only allowed for creating the first admin user
  registerFirstAdmin: (name, email, password) =>
    api.post("/auth/register", { name, email, password }),
  getMe: () => api.get("/auth/me"),
  updateMe: (data) => api.put("/auth/me", data),
  updatePassword: (currentPassword, newPassword) => 
    api.put("/auth/me/password", { current_password: currentPassword, new_password: newPassword }),

  // Setup
  initialize: () => api.post("/setup/initialize"),

  // Loans
  getLoanRegister: () => api.get("/loans/register"),
  getLoanLiability: () => api.get("/loans/liability"),
  assertLoanPayment: (data) => api.post("/loans/asserted-payments", data),

  // Users
  getUsers: (includeDeleted = false) => api.get("/users", { params: { include_deleted: includeDeleted } }),
  createUser: (data) => api.post("/users", data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id, force = false) => api.delete(`/users/${id}`, { params: { force } }),
  getUserAssignments: (id) => api.get(`/users/${id}/assignments`),
  resetUserPassword: (id, newPassword) => api.put(`/auth/users/${id}/password`, { new_password: newPassword }),

  // Categories
  getCategories: () => api.get("/settings/categories"),
  createCategory: (data) => api.post("/settings/categories", data),
  updateCategory: (id, data) => api.put(`/settings/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/settings/categories/${id}`),

  // Statuses
  getStatuses: () => api.get("/settings/statuses"),
  createStatus: (data) => api.post("/settings/statuses", data),
  updateStatus: (id, data) => api.put(`/settings/statuses/${id}`, data),
  deleteStatus: (id) => api.delete(`/settings/statuses/${id}`),

  // Accounts
  getAccounts: () => api.get("/settings/accounts"),
  createAccount: (data) => api.post("/settings/accounts", data),
  updateAccount: (id, data) => api.put(`/settings/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/settings/accounts/${id}`),

  // Vendor profiles (the register: where each supplier's documents come from)
  getVendorProfiles: () => api.get("/vendor-profiles"),
  getVendorProfile: (id) => api.get(`/vendor-profiles/${id}`),
  previewVendorProfile: (criteria) => api.post("/vendor-profiles/preview", criteria),
  suggestVendorPattern: (merchant) =>
    api.get("/vendor-profiles/suggest-pattern", { params: { merchant } }),
  saveVendorProfile: (data, id) =>
    api.post("/vendor-profiles", data, { params: id ? { profile_id: id } : {} }),
  deleteVendorProfile: (id) => api.delete(`/vendor-profiles/${id}`),
  // Credentials are write-only on purpose: there is no endpoint that returns
  // one, so there is no client method that could.
  getVendorAdapters: () => api.get("/vendor-profiles/adapters"),
  getVendorCredential: (id) => api.get(`/vendor-profiles/${id}/credential`),
  setVendorCredential: (id, data) => api.put(`/vendor-profiles/${id}/credential`, data),
  clearVendorCredential: (id) => api.delete(`/vendor-profiles/${id}/credential`),
  fetchVendorInvoices: (id, quarter) =>
    api.post(`/vendor-profiles/${id}/fetch-invoices`, null, { params: quarter ? { quarter } : {} }),
  getMissingReceiptsReport: (quarter) =>
    api.get("/vendor-profiles/reports/missing-receipts", { params: { quarter } }),

  // Transactions
  getTransactions: (params) => api.get("/transactions", { params }),
  getTransaction: (id) => api.get(`/transactions/${id}`),
  updateTransaction: (id, data) => api.put(`/transactions/${id}`, data),
  bulkUpdateTransactions: (data) => api.post("/transactions/bulk-update", data),
  getTransactionSuggestions: (id) => api.get(`/transactions/${id}/suggestions`),
  searchTransactionFiles: (id, q) =>
    api.get(`/transactions/${id}/file-search`, { params: { q } }),
  acceptCategorySuggestion: (id) => api.post(`/transactions/${id}/accept-suggestion`),
  rejectCategorySuggestion: (id) => api.post(`/transactions/${id}/reject-suggestion`),
  toggleFlag: (id) => api.post(`/transactions/${id}/toggle-flag`),
  consolidateTransactions: (txId1, txId2, reason) => api.post("/transactions/consolidate", { 
    transaction_id_1: txId1, 
    transaction_id_2: txId2, 
    reason: reason || "Manual consolidation" 
  }),
  unconsolidateTransactions: (txId1, txId2) => api.post("/transactions/unconsolidate", { 
    transaction_id_1: txId1, 
    transaction_id_2: txId2 
  }),
  
  // Transaction Messages
  getMessages: (transactionId) => api.get(`/transactions/${transactionId}/messages`),
  addMessage: (transactionId, content) => api.post(`/transactions/${transactionId}/messages`, { content }),
  deleteMessage: (transactionId, messageId) => api.delete(`/transactions/${transactionId}/messages/${messageId}`),

  // Reconciliation
  previewReconciliation: () => api.get("/transactions/reconciliation/preview"),
  runReconciliation: () => api.post("/transactions/reconcile"),
  linkMatch: (txId, matchedId, matchType) => api.post(`/transactions/${txId}/link-match`, null, { params: { matched_id: matchedId, match_type: matchType } }),
  unlinkMatch: (txId) => api.delete(`/transactions/${txId}/unlink-match`),

  // Duplicates
  findDuplicates: () => api.get("/transactions/duplicates/find"),
  removeDuplicates: (transactionIds) => api.delete("/transactions/duplicates/remove", { data: transactionIds }),
  removeAllDuplicates: () => api.delete("/transactions/duplicates/remove-all"),

  // Import
  previewImport: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/import/preview", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  confirmImport: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/import/confirm", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getImportBatches: () => api.get("/import/batches"),

  // Settings
  getEmailStatus: () => api.get("/settings/email-status"),

  // Feature Flags
  getFeatureFlags: () => api.get("/settings/features"),
  getFeatureFlag: (key) => api.get(`/settings/features/${key}`),
  updateFeatureFlag: (key, enabled) => api.put(`/settings/features/${key}?enabled=${enabled}`),
  updateAllFeatureFlags: (flags) => api.put("/settings/features", flags),

  // Currency Conversion Settings
  getConversionCurrencies: () => api.get("/settings/currencies"),
  toggleConversionCurrency: (code, enabled) => api.put(`/settings/currencies/${code}?enabled=${enabled}`),
  addConversionCurrency: (code, name, symbol, enabled = true) => 
    api.post(`/settings/currencies?code=${encodeURIComponent(code)}&name=${encodeURIComponent(name)}&symbol=${encodeURIComponent(symbol)}&enabled=${enabled}`),
  removeConversionCurrency: (code) => api.delete(`/settings/currencies/${code}`),

  // Document Analyzer
  analyzeDocument: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/document-analyzer/analyze", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getFieldDefinitions: (formatType) => api.get(`/document-analyzer/field-definitions/${formatType}`),
  
  // Custom Field Definitions
  getCustomFields: () => api.get("/document-analyzer/custom-fields"),
  addCustomField: (fieldId, label, pattern, description, color, importance, formatType) => 
    api.post(`/document-analyzer/custom-fields?field_id=${encodeURIComponent(fieldId)}&label=${encodeURIComponent(label)}&pattern=${encodeURIComponent(pattern)}&description=${encodeURIComponent(description || '')}&color=${encodeURIComponent(color || '#6B7280')}&importance=${encodeURIComponent(importance || 'medium')}&format_type=${encodeURIComponent(formatType || 'wise')}`),
  updateCustomField: (fieldId, updates) => {
    const params = new URLSearchParams();
    if (updates.label) params.append('label', updates.label);
    if (updates.pattern) params.append('pattern', updates.pattern);
    if (updates.description !== undefined) params.append('description', updates.description);
    if (updates.color) params.append('color', updates.color);
    if (updates.importance) params.append('importance', updates.importance);
    return api.put(`/document-analyzer/custom-fields/${fieldId}?${params.toString()}`);
  },
  deleteCustomField: (fieldId) => api.delete(`/document-analyzer/custom-fields/${fieldId}`),

  // Files
  uploadFile: (file, tags) => {
    const formData = new FormData();
    formData.append("file", file);
    if (tags) formData.append("tags", tags);
    return api.post("/files/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getFiles: (params) => api.get("/files", { params }),
  getFile: (id) => api.get(`/files/${id}`),
  deleteFile: (id) => api.delete(`/files/${id}`),
  analyzeFile: (id) => api.post(`/files/${id}/analyze`),
  unpackEmail: (id) => api.post(`/files/${id}/unpack`),
  toggleFileFlag: (id) => api.post(`/files/${id}/toggle-flag`),
  setFileStatus: (id, status) => api.post(`/files/${id}/set-status?status=${status}`),
  resetNoTransactionFiles: () => api.post("/files/reset-no-transaction"),
  getFileSuggestions: (id) => api.get(`/files/${id}/suggestions`),
  getFileLinkedTransactions: (id) => api.get(`/files/${id}/linked-transactions`),
  getMatchQueue: (params) => api.get("/files/match-queue", { params }),
  downloadBatchReceipts: (startDate, endDate, onlyUnexported = false) =>
    api.get("/files/download-batch", {
      params: { start_date: startDate, end_date: endDate, only_unexported: onlyUnexported },
      responseType: 'blob'
    }),

  // Attachments
  attachFile: (transactionId, fileId) => {
    const formData = new FormData();
    formData.append("file_id", fileId);
    return api.post(`/transactions/${transactionId}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  detachFile: (transactionId, fileId) =>
    api.delete(`/transactions/${transactionId}/attachments/${fileId}`),

  // FX Rates
  getFXRates: () => api.get("/fx-rates"),
  createFXRate: (data) => api.post("/fx-rates", data),
  convertCurrency: (amount, from, to, date) =>
    api.get("/fx-rates/convert", {
      params: {
        amount,
        from_currency: from,
        to_currency: to,
        date,
      },
    }),

  // Analytics
  getDashboardAnalytics: (quarter) =>
    api.get("/analytics/dashboard", { params: { quarter } }),
  getExpensesDetail: (quarter, limit) =>
    api.get("/analytics/expenses-detail", { params: { quarter, limit } }),
  getMyTasks: () => api.get("/analytics/my-tasks"),
  
  // MCP (Model Context Protocol) identity management
  getMcpRoles: () => api.get("/mcp/roles"),
  getMcpTools: () => api.get("/mcp/tools"),
  getMcpIdentities: (includeRevoked = false) =>
    api.get("/mcp/identities", { params: { include_revoked: includeRevoked } }),
  createMcpIdentity: (data) => api.post("/mcp/identities", data),
  updateMcpIdentityRole: (id, role) => api.put(`/mcp/identities/${id}/role`, { role }),
  revokeMcpIdentity: (id) => api.delete(`/mcp/identities/${id}`),

  // Danger Zone
  clearAllTransactions: () => api.delete("/settings/danger/clear-all-transactions"),
  
  // Reanalyze
  reanalyzeUnlinkedFiles: () => api.post("/files/reanalyze-unlinked"),

  // Ecobank payment-instruction letter
  getEcobankDefaults: () => api.get("/ecobank/defaults"),
  extractEcobankInvoices: (graFile, ssnitFile) => {
    const formData = new FormData();
    if (graFile) formData.append("gra_invoice", graFile);
    if (ssnitFile) formData.append("ssnit_invoice", ssnitFile);
    return api.post("/ecobank/extract", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  generateEcobankLetter: (data, format) =>
    api.post("/ecobank/generate", data, {
      params: { format },
      responseType: "blob",
    }),
};

export default api;
